import {
  IOrderTemplate,
  OrderTemplate,
  OrderTemplateLine,
  IItem,
  ISupplier,
  IBrand,
  IUnit,
  SalesTax,
} from '$models'
import {
  PurchaseOrderUi,
  PurchaseOrderUiLine,
} from '../../purchase-orders/types'
import { stockCostService } from '$services'
import { exceptionMessageHandler } from '$utils'
import useStockStore from '../../../store'
import dayjs from 'dayjs'

export function convertOrderTemplateToPurchaseOrderUi(
  orderTemplate: IOrderTemplate | null,
  items: Record<string, IItem>,
  salesTax: SalesTax[]
) {
  const purchaseOrderUi: PurchaseOrderUi = {
    id: orderTemplate?.id,
    templateName: orderTemplate?.name,
    createdAt: orderTemplate?.createdAt,
    creditRequest: orderTemplate?.creditRequest,
    lines:
      orderTemplate?.lines?.map((line) => {
        const item = line.stockItemId ? items[line.stockItemId] : undefined
        const taxSortOrder = item?.globalSalesTaxSortOrder
        const taxRate =
          taxSortOrder != null ? salesTax?.[taxSortOrder]?.rate : undefined

        return {
          ...line,
          lineId: line.id ?? crypto.randomUUID(),
          itemCode: line.stockCode,
          itemId: line.stockItemId,
          itemName: line.stockItemName,
          brandId: line.brandId,
          supplierId: line.supplierId,
          unitId: line.unitId,
          quantityOrdered: line.quantity,
          unitRatio: line.unitRatio,
          __reorder__: line.stockCode ?? '',
          isEstimatedCostLoading: !!line.stockItemId,
          taxPercent: taxRate,
          selectedSalesTax: taxSortOrder,
          customTaxRate: taxRate != null ? taxRate * 100 : 0,
        }
      }) ?? [],
  }
  return purchaseOrderUi
}

export function convertPurchaseOrderUiToOrderTemplate(
  purchaseOrderUi: PurchaseOrderUi,
  items?: Record<string, IItem>,
  suppliers?: Record<string, ISupplier>,
  brands?: Record<string, IBrand>,
  units?: Record<string, IUnit>
): OrderTemplate {
  const lines = purchaseOrderUi?.lines?.map((uiLine, index) => {
    return new OrderTemplateLine({
      id: uiLine.id,
      templateId: purchaseOrderUi.id,
      stockItemId: uiLine.itemId,
      stockItemName:
        (uiLine.itemId && items?.[uiLine.itemId]?.name) || uiLine.itemName,
      supplierId: uiLine.supplierId,
      supplierName:
        (uiLine.supplierId && suppliers?.[uiLine.supplierId]?.name) || '',
      brandId: uiLine.brandId,
      brandName:
        (uiLine.brandId && brands?.[uiLine.brandId]?.name) || uiLine.brandName,
      unitId: uiLine.unitId,
      unitName:
        (uiLine.unitId && units?.[uiLine.unitId]?.name) || uiLine.unitName,
      unitRatio: uiLine.unitRatio,
      stockCode: uiLine.itemCode || undefined,
      unitCost: (uiLine?.unitCostOrdered || 0) * (uiLine?.unitRatio || 1),
      quantity: uiLine?.quantityOrdered || 0,
      lineOrder: index,
    })
  })

  const orderTemplate = new OrderTemplate({
    id: purchaseOrderUi.id,
    name: purchaseOrderUi.templateName,
    createdAt: purchaseOrderUi.createdAt,
    creditRequest: purchaseOrderUi.creditRequest,
    lines: lines,
  })

  return orderTemplate
}

/**
 * Fetches live prices for the provided template lines in a single batched call
 * and populates each line's unitCostOrdered. Lines render with empty cost and
 * a spinner until this resolves.
 */
export async function addOrderTemplateLineCosts(
  lines: PurchaseOrderUiLine[] | undefined
): Promise<void> {
  const linesWithItem = (lines ?? []).filter((line) => !!line.itemId)
  if (linesWithItem.length === 0) return

  const { updateEstimatedCost, setEstimatedCostLoading } =
    useStockStore.getState()

  const now = dayjs().toISOString()
  const itemIdTimeStrings = linesWithItem.map((line) => `${line.itemId},${now}`)

  try {
    const stockCostData = await stockCostService.getStockCost(itemIdTimeStrings)
    const itemCosts = stockCostData.itemCosts ?? {}

    linesWithItem.forEach((line) => {
      if (!line.itemId) return
      const firstStockCost = itemCosts[line.itemId]?.at(0)
      updateEstimatedCost(firstStockCost?.cost ?? 0, line.lineId)
      setEstimatedCostLoading(line.lineId, false)
    })
  } catch (error) {
    linesWithItem.forEach((line) => {
      setEstimatedCostLoading(line.lineId, false)
    })
    exceptionMessageHandler(error, 'Error fetching cost data')
  }
}
